import _ from 'lodash';
import * as stateManagement from '#packages/stateManagement';
import * as coreBi from '#packages/coreBi';
import * as spotlight from './createNodeProps/spotlight';
import * as util from '#packages/util';
import constants from '#packages/constants';
import { NOTIFICATIONS } from '#packages/helpIds';
import {
  hasComponentChildrenToShowInLayersPanel,
  getComponentChildrenToShowInLayersPanel_GENERATOR,
} from './createNodeProps/nodeChildrenGenerator';

import type { EditorAPI } from '#packages/editorAPI';
import type { CompRef, CompLayout } from 'types/documentServices';
import type { NodeProps } from './node';
import type { TreeNodesContext } from './createNodeProps/nodesContext';
import {
  getAllNodesEmptyStateContent,
  scrollToCompAndSelect,
  isComponentWithinPage,
} from './createNodeProps/createNodeProps.utils';

interface OutOfGridComps {
  [key: string]: boolean;
}

const getPanelOrigin = (editorAPI: EditorAPI) => {
  const appState = editorAPI.store.getState();
  const panelOrigin =
    stateManagement.layersPanel.selectors.getOrigin(appState) ||
    constants.PANELS.SECTIONS.SECTIONS_LAYERS_PANEL;

  return panelOrigin;
};

const getPanelView = (editorAPI: EditorAPI) => {
  const openPanels = editorAPI.panelManager.getOpenPanels();
  const isSectionsLayersPanelOpen = openPanels.some(
    (panel) => panel.name === constants.PANELS.SECTIONS.SECTIONS_LAYERS_PANEL,
  );

  return isSectionsLayersPanelOpen ? 'minimized' : 'full';
};

const moveToIndex = (
  editorAPI: EditorAPI,
  children: CompRef[],
  biData: { label: string; comp: CompRef },
  from: number,
  to: number,
) => {
  editorAPI.components.arrangement.moveToIndex(children[from], to);

  editorAPI.store.dispatch(
    stateManagement.bi.actions.event(coreBi.events.layers.layer_drag, {
      ...editorAPI.bi.getComponentsBIParams([children[from]])[0],
      panel_origin: getPanelOrigin(editorAPI),
      panel_view: getPanelView(editorAPI),
      previous_position_index: from,
      current_position_index: to,
      layer_name: biData.label,
      out_of_grid:
        !!editorAPI.components.layout.shouldShowOutOfGridlinesIndication(
          children[from],
        ),
      out_of_page: !isComponentWithinPage(editorAPI, children[from]),
    }),
  );
};

const toggleVisibility = (
  editorAPI: EditorAPI,
  compRef: CompRef,
  changeVisibilityFunc: Function,
) => {
  changeVisibilityFunc(compRef);
  editorAPI.dsRead.components.get
    .byAncestor(compRef)
    .forEach((childCompRef) => {
      changeVisibilityFunc(childCompRef);
    });

  spotlight.setSpotlightComponent(editorAPI, null);
};

const getComponentDisplayName = (
  editorAPI: EditorAPI,
  compRef: CompRef,
  level: number,
): string => {
  if (level === 0) {
    return editorAPI.components.getDisplayName(compRef);
  }

  const codeId =
    editorAPI.developerMode.isEnabled() &&
    editorAPI.components.getNickname(compRef);

  if (codeId) {
    return codeId;
  }

  return (
    util.componentLabel.getComponentLabel(editorAPI, compRef) ||
    editorAPI.components.getDisplayName(compRef)
  );
};

const isCompDescendantOfMobileMenu = (comp: CompRef, editorAPI: EditorAPI) => {
  const mobileMenuCompRef = editorAPI.components.get.byId('MENU_AS_CONTAINER');
  const isDescendantOfComp = editorAPI.components.isDescendantOfComp(
    comp,
    mobileMenuCompRef,
  );
  const isSameId = mobileMenuCompRef?.id === comp.id;
  return isDescendantOfComp || isSameId;
};

const handleMobileMenu = (editorAPI: EditorAPI, comp: CompRef) => {
  const menuIconCompRef = editorAPI.components.get.byId(
    'MENU_AS_CONTAINER_TOGGLE',
  );

  const target = util.inlinePopupUtils.getToggleTarget(
    editorAPI,
    menuIconCompRef,
  );
  const isOpen = target && util.inlinePopupUtils.isOpen(editorAPI, target);
  if (isCompDescendantOfMobileMenu(comp, editorAPI)) {
    if (!isOpen) {
      editorAPI.mobile.toggleMobileMenu(target);
    }
  } else if (isOpen) {
    editorAPI.mobile.toggleMobileMenu(target);
  }
};

const onNodeClick = (
  editorAPI: EditorAPI,
  comp: CompRef,
  biData = { label: '' },
) => {
  if (editorAPI.isMobileEditor()) {
    handleMobileMenu(editorAPI, comp);
  }
  scrollToCompAndSelect(editorAPI, comp);
  editorAPI.store.dispatch(
    stateManagement.bi.actions.event(coreBi.events.layers.layer_click, {
      ...editorAPI.bi.getComponentsBIParams([comp])[0],
      panel_origin: getPanelOrigin(editorAPI),
      panel_view: getPanelView(editorAPI),
      layer_name: biData.label,
      is_landing_page: editorAPI.isCurrentPageLandingPage(),
      is_popup: editorAPI.dsRead.pages.popupPages.isPopupOpened(),
      out_of_grid:
        !!editorAPI.components.layout.shouldShowOutOfGridlinesIndication(comp),
      out_of_page: !isComponentWithinPage(editorAPI, comp),
    }),
  );
};

const onToggleVisibility = (
  editorAPI: EditorAPI,
  comp: CompRef,
  biData = { label: '' },
) => {
  const { renderPlugins } = editorAPI.dsActions;
  const isHidden = editorAPI.dsRead.renderPlugins.isCompHidden(comp);
  toggleVisibility(
    editorAPI,
    comp,
    isHidden ? renderPlugins.showComp : renderPlugins.hideComp,
  );
  editorAPI.store.dispatch(
    stateManagement.bi.actions.event(coreBi.events.layers.layer_show_hide, {
      ...editorAPI.bi.getComponentsBIParams([comp])[0],
      panel_origin: getPanelOrigin(editorAPI),
      panel_view: getPanelView(editorAPI),
      state: isHidden ? '1' : '0',
      layer_name: biData.label,
      out_of_grid:
        !!editorAPI.components.layout.shouldShowOutOfGridlinesIndication(comp),
      out_of_page: !isComponentWithinPage(editorAPI, comp),
    }),
  );
};

const onToggleSpotlight = (
  editorAPI: EditorAPI,
  comp: CompRef,
  biData = { label: '' },
) => {
  const isComponentSpotlighted = spotlight.isComponentSpotlighted(
    editorAPI,
    comp,
  );
  if (!isComponentSpotlighted) {
    scrollToCompAndSelect(editorAPI, comp);
  }
  const state = isComponentSpotlighted ? '0' : '1';
  spotlight.toggleSpotlight(editorAPI, comp);
  editorAPI.store.dispatch(
    stateManagement.bi.actions.event(coreBi.events.layers.layer_spotlight, {
      ...editorAPI.bi.getComponentsBIParams([comp])[0],
      state,
      panel_origin: getPanelOrigin(editorAPI),
      panel_view: getPanelView(editorAPI),
      layer_name: biData.label,
      out_of_grid:
        !!editorAPI.components.layout.shouldShowOutOfGridlinesIndication(comp),
      out_of_page: !isComponentWithinPage(editorAPI, comp),
    }),
  );
};

const isDevModeEnabled = (editorAPI: EditorAPI) => {
  return editorAPI.developerMode.isEnabled();
};

let outOfGridComps: OutOfGridComps = {};
let isMobileNode = false;

const shouldShowWarningIcon = (comp: CompRef) => {
  return outOfGridComps[comp.id];
};

const getParentLayout = (editorAPI: EditorAPI, comp: CompRef) => {
  const parentRef = editorAPI.components.getContainer(comp);
  return editorAPI.components.layout.getRelativeToStructure(parentRef);
};

const getComponentNewPosition = (
  compLayout: CompLayout,
  parentLayout: CompLayout,
) => ({
  x: parentLayout.x + parentLayout.width / 2 - compLayout.width / 2,
  y: parentLayout.y + parentLayout.height / 2 - compLayout.height / 2,
});

const handleRepositionComponentInParent = (
  editorAPI: EditorAPI,
  comp: CompRef,
) => {
  const compLayout = editorAPI.components.layout.getRelativeToStructure(comp);
  const parentLayout = getParentLayout(editorAPI, comp);
  const componentNewPosition = getComponentNewPosition(
    compLayout,
    parentLayout,
  );

  // eslint-disable-next-line @wix/santa-editor/deprecatedDSApi
  editorAPI.components.layout.updateRelativeToStructure(
    comp,
    componentNewPosition,
  );
  editorAPI.dsActions.waitForChangesAppliedAsync().then(() => {
    editorAPI.components.arrangement.moveToFront(comp);
    scrollToCompAndSelect(editorAPI, comp);
  });
  editorAPI.store.dispatch(
    stateManagement.bi.actions.event(coreBi.events.layers.recenter_element, {
      ...editorAPI.bi.getComponentsBIParams([comp])[0],
      origin: getPanelOrigin(editorAPI),
      panel_view: getPanelView(editorAPI),
    }),
  );
};

interface CreateNodePropsOptions {
  isSOAP?: boolean;
  level?: number;
  parent?: NodeProps;
}

const createNodeProps = (
  editorAPI: EditorAPI,
  comp: CompRef,
  { isSOAP = false, level = 0, parent = null }: CreateNodePropsOptions = {},
  treeNodesContext: TreeNodesContext,
): NodeProps => {
  const isSpotlight = spotlight.isComponentSpotlighted(editorAPI, comp);
  const label = getComponentDisplayName(editorAPI, comp, level);

  const [selectedComp] = editorAPI.selection.getSelectedComponents();
  const hasChildNodes = hasComponentChildrenToShowInLayersPanel(
    editorAPI,
    comp,
  );
  const allNodesEmptyStateContent = getAllNodesEmptyStateContent(
    editorAPI,
    comp,
    hasChildNodes,
  );

  const emptyStateData =
    allNodesEmptyStateContent[
      comp.id as keyof typeof allNodesEmptyStateContent
    ];

  const areChildNodesExpandedByDefault = () =>
    level < 1 ||
    (selectedComp &&
      editorAPI.components.isAncestorOfCompOrCompScope(comp, selectedComp));
  const areChildNodesExpanded =
    (hasChildNodes || emptyStateData) &&
    (treeNodesContext.isNodeExpanded(comp.id) ??
      areChildNodesExpandedByDefault());

  if (isMobileNode !== editorAPI.isMobileEditor()) {
    isMobileNode = editorAPI.isMobileEditor();
    outOfGridComps = {};
  }

  const setOutOfGridComps = _.throttle(
    (editorAPI: EditorAPI, comp: CompRef) => {
      const isDragging =
        !!editorAPI.mouseActions.getRegisteredMouseMoveAction();
      const compIsDescendantOfSelected =
        selectedComp?.id === comp.id ||
        editorAPI.components.isDescendantOfComp(comp, selectedComp);
      const shouldSetOutOfGridComps =
        outOfGridComps[comp.id] === undefined ||
        (!isDragging && compIsDescendantOfSelected);

      if (
        shouldSetOutOfGridComps &&
        !isCompDescendantOfMobileMenu(comp, editorAPI)
      ) {
        outOfGridComps[comp.id] =
          editorAPI.components.layout.shouldShowOutOfGridlinesIndication(comp);
      }
    },
    100,
    { trailing: true },
  );

  setOutOfGridComps(editorAPI, comp);

  const nodeProps: NodeProps = {
    parent,
    id: comp.id,
    level,
    label,
    panelView: getPanelView(editorAPI),
    isSelected: editorAPI.utils.isSameRef(comp, selectedComp),
    isHidden: editorAPI.documentServices.renderPlugins.isCompHidden(comp),
    onClick: onNodeClick.bind(null, editorAPI, comp, { label }),
    onToggleVisibility: onToggleVisibility.bind(null, editorAPI, comp, {
      label,
    }),
    onToggleSpotlight: onToggleSpotlight.bind(null, editorAPI, comp, { label }),
    shouldShowWarningIcon: shouldShowWarningIcon(comp),
    icon: editorAPI.components.getIconInfo(comp, null, 'layers'),
    isSpotlight,
    selectedCompId: selectedComp?.id,
    isSOAP,
    isDevModeEnabled: isDevModeEnabled(editorAPI),
    sendBi: editorAPI.bi.event,
    componentBIParams: editorAPI.bi.getComponentsBIParams([comp])[0],
    panelOrigin: getPanelOrigin(editorAPI),
    componentType: editorAPI.components.getType(comp),
    isLandingPage: editorAPI.isCurrentPageLandingPage(),
    isLightBoxMode: editorAPI.dsRead.pages.popupPages.isPopupOpened(),
    openOutOfGridHelpCenter: () => {
      editorAPI.panelManager.openHelpCenter(NOTIFICATIONS.OUT_OF_GRIDLINES);
      editorAPI.store.dispatch(
        stateManagement.bi.actions.event(coreBi.events.help.HELP_CLICK, {
          origin: 'layers_panel',
        }),
      );
    },
    hasChildNodes,
    areChildNodesExpanded,
    setChildNodesExpanded: (nextIsExpanded: boolean) => {
      treeNodesContext.setIsNodeExpanded(comp.id, nextIsExpanded);
    },
    emptyStateData,
    isComponentWithinPage: isComponentWithinPage(editorAPI, comp),
    repositionComponentInParent: () => {
      handleRepositionComponentInParent(editorAPI, comp);
    },
  };

  if (nodeProps.hasChildNodes && nodeProps.areChildNodesExpanded) {
    const filteredChildren = Array.from(
      getComponentChildrenToShowInLayersPanel_GENERATOR(editorAPI, comp),
    );

    nodeProps.childNodes = filteredChildren.map((childCompRef) =>
      createNodeProps(
        editorAPI,
        childCompRef,
        { isSOAP, level: level + 1, parent: nodeProps },
        treeNodesContext,
      ),
    );

    nodeProps.moveToIndex = moveToIndex.bind(
      null,
      editorAPI,
      filteredChildren,
      { label, comp },
    );
  }

  return nodeProps;
};

export default createNodeProps;
